import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Container,
    Button,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { authService } from '../../services/authService';

export const VerifyEmailPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');

    // Extract token as a stable string — useSearchParams() returns a new object
    // reference on every render, so using it directly as a dep would re-fire the
    // effect after setStatus() triggers a re-render, causing a double-call that
    // makes the (already-consumed) token appear invalid.
    const token = searchParams.get('token');

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token) {
                setStatus('error');
                setMessage(t('auth.verifyEmail.error'));
                return;
            }

            try {
                const response = await authService.verifyEmail(token);
                setStatus('success');
                setMessage(response.message || t('auth.verifyEmail.success'));
            } catch (err: any) {
                setStatus('error');
                setMessage(err.response?.data?.detail || t('auth.verifyEmail.error'));
            }
        };

        verifyEmail();
    }, [token, t]);

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {t('auth.verifyEmail.title')}
                    </Typography>

                    {status === 'loading' && (
                        <Box sx={{ mt: 4 }}>
                            <CircularProgress size={60} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                {t('auth.verifyEmail.verifying')}
                            </Typography>
                        </Box>
                    )}

                    {status === 'success' && (
                        <Box sx={{ mt: 4 }}>
                            <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
                            <Alert severity="success" sx={{ mt: 2 }}>
                                {message}
                            </Alert>
                            <Button
                                variant="contained"
                                sx={{ mt: 3 }}
                                onClick={() => navigate('/login')}
                            >
                                {t('auth.login.submit')}
                            </Button>
                        </Box>
                    )}

                    {status === 'error' && (
                        <Box sx={{ mt: 4 }}>
                            <Error sx={{ fontSize: 80, color: 'error.main' }} />
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {message}
                            </Alert>
                            <Button
                                variant="outlined"
                                sx={{ mt: 3 }}
                                onClick={() => navigate('/login')}
                            >
                                {t('auth.forgotPassword.backToLogin')}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};
